import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import InfoModal from "@commons/Modal/InfoModal";
import { usePage } from "@hooks/usePage";
import {
  validateCoordinates,
  validateLatitude,
  validateLongitude,
} from "@utils/formatters";
import TextInput from "@commons/Input/Text";
import Map from "@commons/Map";
import PublicIcon from "@mui/icons-material/Public";
import Heading from "@commons/Typography/Heading";
import Text from "@commons/Typography/Text";
import { useEffect, useState } from "react";
import { createRelease } from "@services/releases/create";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import ComboBox from "@commons/Input/ComboBox";
import { searchIndividuals } from "@services/individuals/search";
import { searchPpmaUsers } from "@services/users/search";
import {
  ComboBoxDefault,
  type ComboBoxOption,
} from "@typings/components/comboBox";
import ListPreview from "@commons/Preview/ListPreview";
import type { Individual } from "@typings/entities/individuals";
import { globalMutate } from "@utils/swr";
import { useModalOpenWithCoordinates } from "@hooks/useModalOpenWithCoordinates";

const ReleaseGroupsSchema = z.object({
  individual: z.object({
    label: z.string().min(1, "Indivíduo é obrigatório"),
    value: z.string().min(1, "Indivíduo é obrigatório"),
  }),
  expectedDate: z.iso.date("Data de soltura inválida"),
  executor: z.object({
    label: z.string().min(1, "Agente designado é obrigatório"),
    value: z.string().min(1, "Agente designado é obrigatório"),
  }),
  latitude: z
    .string()
    .min(1, "Latitude é obrigatória")
    .refine(validateLatitude(), {
      message: "Latitude deve estar entre -90 e 90",
    }),
  longitude: z
    .string()
    .min(1, "Longitude é obrigatória")
    .refine(validateLongitude(), {
      message: "Longitude deve estar entre -180 e 180",
    }),
});

type ReleaseGroupsFormData = z.infer<typeof ReleaseGroupsSchema>;

function ModalNewRelease() {
  const { isModalOpen, closeModal, setDataModal, setIsLoading } = usePage();

  const [individualDetails, setIndividualDetails] = useState<Individual | null>(
    null,
  );
  const [showNotificationWarning, setShowNotificationWarning] = useState(false);
  const open = isModalOpen("newRelease");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ReleaseGroupsFormData>({
    resolver: zodResolver(ReleaseGroupsSchema),
    defaultValues: {
      individual: ComboBoxDefault,
      executor: ComboBoxDefault,
      latitude: "",
      longitude: "",
    },
  });

  const selectedIndividual = watch(
    "individual",
  ) as ComboBoxOption<Individual> | null;

  useModalOpenWithCoordinates<ReleaseGroupsFormData>({ open, setValue });

  useEffect(() => {
    if (selectedIndividual?.value) {
      setIndividualDetails(selectedIndividual.item || null);
    } else {
      setIndividualDetails(null);
    }
  }, [selectedIndividual]);

  const onSubmit = async (data: ReleaseGroupsFormData) => {
    try {
      setIsLoading(true);

      const releaseCreated = await createRelease({
        expectedDate: data.expectedDate,
        executorId: data.executor.value,
        individualId: data.individual.value,
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
      });

      toast.success("Plano de soltura criado com sucesso");
      globalMutate(["fetch-releases-geojson", "fetch-get-releases"]);

      if (releaseCreated.notificationsOk === false) {
        setShowNotificationWarning(true);
      } else {
        handleClose();
      }
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError
        ? error.message
        : "Erro ao criar plano de soltura";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setIndividualDetails(null);
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <>
      <Modal
        open={open}
        heading="Criar novo plano de soltura"
        onClose={handleClose}
        onConfirm={handleSubmit(onSubmit)}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        className="max-w-4xl"
      >
        <form className="flex flex-col sm:flex-row gap-12">
          <div className="flex flex-col gap-4 sm:gap-6 w-full">
            <Controller
              name="individual"
              control={control}
              render={({ field }) => (
                <ComboBox<Individual>
                  {...field}
                  value={field.value}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={(query) =>
                    searchIndividuals(query, { situation: "in_captivity" })
                  }
                  label="Código do Indivíduo"
                  error={!!errors.individual}
                  helperText={errors.individual?.message}
                />
              )}
            />

            {individualDetails && (
              <div className="flex flex-col gap-2">
                <Heading size="sm">Dados do Indivíduo Selecionado</Heading>
                <ListPreview.Root>
                  <ListPreview.Row
                    label="N° Ficha:"
                    value={individualDetails.clinicalRecord}
                  />
                  <ListPreview.Row
                    label="Nome Comum:"
                    value={individualDetails.commonName}
                  />
                  <ListPreview.Row
                    label="Nome Científico:"
                    value={individualDetails.taxonomicGroup.species}
                  />
                  <ListPreview.Row
                    label="Nome UFU:"
                    value={individualDetails.ufuName}
                  />
                </ListPreview.Root>
              </div>
            )}

            <Controller
              name="expectedDate"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Data de Soltura"
                  textSize="sm"
                  type="date"
                  fullWidth
                  error={!!errors.expectedDate}
                  helperText={errors.expectedDate?.message}
                />
              )}
            />

            <Controller
              name="executor"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  value={
                    field.value?.value
                      ? {
                          label: field.value.label,
                          value: field.value.value,
                        }
                      : null
                  }
                  onChange={(option) => {
                    field.onChange(option || ComboBoxDefault);
                  }}
                  searchService={searchPpmaUsers}
                  label="Agente Designado"
                  error={!!errors.executor}
                  helperText={errors.executor?.message}
                />
              )}
            />
            <div>
              <Heading size="sm" className="mb-4">
                Coordenadas de Soltura
              </Heading>
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-y-4 xs:gap-x-2 sm:gap-x-3">
                <Controller
                  name="latitude"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Latitude"
                      type="number"
                      textSize="sm"
                      fullWidth
                      error={!!errors.latitude}
                      helperText={errors.latitude?.message}
                    />
                  )}
                />

                <Controller
                  name="longitude"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Longitude"
                      type="number"
                      textSize="sm"
                      fullWidth
                      error={!!errors.longitude}
                      helperText={errors.longitude?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="h-90 w-full">
            <Heading size="sm" className="mb-4">
              Visualização das coordenadas
            </Heading>

            {hasValidCoordinates ? (
              <Map.Root>
                <Map.Localization latitude={lat} longitude={lng} draw />
              </Map.Root>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
                <PublicIcon className="text-4xl" />
                <Text size="sm">
                  Insira coordenadas válidas para visualizar o mapa.
                </Text>
              </div>
            )}
          </div>
        </form>
      </Modal>

      <InfoModal
        open={showNotificationWarning}
        title="Aviso sobre Notificações"
        message="O plano de soltura foi criado com sucesso, mas não foi possível enviar as notificações aos responsáveis. Por favor, avise o agente designado."
        onClose={() => {
          setShowNotificationWarning(false);
          handleClose();
        }}
        buttonLabel="Entendi"
      />
    </>
  );
}

export default ModalNewRelease;
